import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Check, X } from '@lifeomic/chromicons';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Select, SelectOption } from '@lifeomic/chroma-react/components/Select';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { TextArea } from '@lifeomic/chroma-react/components/TextArea';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import { useTheme } from '@lifeomic/chroma-react/styles';
import { useActiveProjectId, useNotify } from '../../hooks';
import { useApiClient } from '../../hooks/useRestAPIHooks';
import { isReviewableResourceType } from '../../util/extractedResourceReview';
import { getTestProps } from '../../util/testProps';
import {
  heroChipStyle,
  heroChipTone,
  heroSpace,
  heroSurface,
} from './memberExtractionTheme';
import {
  buildRejectionFeedback,
  getRejectionReasons,
  isRejectionComplete,
  MemberExtractionRejectionReasonId,
  parseRejectionSource,
} from './memberExtractionRejectionReasons';
import type {
  MemberExtractionResourceRow,
  MemberExtractionReviewDecision,
} from './useMemberExtractionDocuments';

const messages = defineMessages({
  approve: {
    id: 'phc-web-toolkit.memberExtractions.review.approve',
    defaultMessage: 'Approve',
  },
  reject: {
    id: 'phc-web-toolkit.memberExtractions.review.reject',
    defaultMessage: 'Reject',
  },
  approved: {
    id: 'phc-web-toolkit.memberExtractions.review.approved',
    defaultMessage: 'Approved',
  },
  rejected: {
    id: 'phc-web-toolkit.memberExtractions.review.rejected',
    defaultMessage: 'Rejected',
  },
  cancel: {
    id: 'phc-web-toolkit.memberExtractions.review.cancel',
    defaultMessage: 'Cancel',
  },
  approveTitle: {
    id: 'phc-web-toolkit.memberExtractions.review.approveTitle',
    defaultMessage: 'Approve extraction?',
  },
  rejectTitle: {
    id: 'phc-web-toolkit.memberExtractions.review.rejectTitle',
    defaultMessage: 'Reject extraction?',
  },
  reasonLabel: {
    id: 'phc-web-toolkit.memberExtractions.review.reasonLabel',
    defaultMessage: 'Reason',
  },
  reasonPlaceholder: {
    id: 'phc-web-toolkit.memberExtractions.review.reasonPlaceholder',
    defaultMessage: 'Choose a reason',
  },
  approveMessage: {
    id: 'phc-web-toolkit.memberExtractions.review.approveMessage',
    defaultMessage:
      "This result will stay on the member's record and be marked as reviewed.",
  },
  rejectMessage: {
    id: 'phc-web-toolkit.memberExtractions.review.rejectMessage',
    defaultMessage:
      "This result will be removed from the member's record. This can't be undone.",
  },
  approveSuccess: {
    id: 'phc-web-toolkit.memberExtractions.review.approveSuccess',
    defaultMessage: 'Extraction approved.',
  },
  rejectSuccess: {
    id: 'phc-web-toolkit.memberExtractions.review.rejectSuccess',
    defaultMessage: 'Extraction rejected and removed.',
  },
  error: {
    id: 'phc-web-toolkit.memberExtractions.review.error',
    defaultMessage: 'Unable to save extraction feedback. Please try again.',
  },
});

const actionsStyle = {
  display: 'flex',
  alignItems: 'center',
  alignSelf: 'center',
  gap: heroSpace.tight,
  marginLeft: 'auto',
  flex: '0 0 auto',
};

const confirmContentStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: heroSpace.regular,
};

const resultSummaryStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  padding: heroSpace.regular,
  borderRadius: heroSurface.radiusMedium,
  backgroundColor: heroSurface.muted,
};

export type MemberExtractionReviewedHandler = (
  resource: MemberExtractionResourceRow,
  decision: MemberExtractionReviewDecision,
) => void;

type MemberExtractionResultReviewProps = {
  patientId: string;
  sourceReference: string;
  resource: MemberExtractionResourceRow;
  onReviewed?: MemberExtractionReviewedHandler;
};

export const MemberExtractionResultReview: React.FC<
  MemberExtractionResultReviewProps
> = ({ patientId, sourceReference, resource, onReviewed }) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const projectId = useActiveProjectId();
  const apiClient = useApiClient();
  const [notify] = useNotify();
  const [decided, setDecided] =
    React.useState<MemberExtractionReviewDecision>();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmDecision, setConfirmDecision] =
    React.useState<MemberExtractionReviewDecision>('approved');
  const [decisionInFlight, setDecisionInFlight] =
    React.useState<MemberExtractionReviewDecision>();
  const [reasonId, setReasonId] =
    React.useState<MemberExtractionRejectionReasonId>();
  const [reasonDetail, setReasonDetail] = React.useState('');
  const reasons = React.useMemo(
    () => getRejectionReasons(resource),
    [resource],
  );
  const reason = reasons.find(item => item.id === reasonId);

  const status = resource.reviewDecision ?? decided;
  if (status) {
    const StatusIcon = status === 'approved' ? Check : X;
    return (
      <span
        style={{
          ...heroChipStyle,
          ...(status === 'approved'
            ? heroChipTone.success
            : heroChipTone.danger),
          ...actionsStyle,
        }}
        {...getTestProps('member-extraction-review-status')}
      >
        <StatusIcon aria-hidden width={12} height={12} />
        {formatMessage(
          status === 'approved' ? messages.approved : messages.rejected,
        )}
      </span>
    );
  }
  if (!isReviewableResourceType(resource.resourceType)) {
    return null;
  }

  const isReject = confirmDecision === 'rejected';
  const canSubmit =
    !decisionInFlight &&
    (!isReject || isRejectionComplete(reason, reasonDetail));

  const submit = (decision: MemberExtractionReviewDecision) => {
    if (!projectId || decisionInFlight) {
      return;
    }
    if (decision === 'rejected' && !isRejectionComplete(reason, reasonDetail)) {
      return;
    }
    setDecisionInFlight(decision);
    // The source link ties the rejection to its document so a forced
    // re-extraction can feed it back; the published client type omits it.
    const feedback = {
      resource_type: resource.resourceType,
      resource_id: resource.id,
      subject_id: patientId,
      project_id: projectId,
      decision,
      ...(decision === 'rejected' && reason
        ? {
            ...buildRejectionFeedback(reason, reasonDetail),
            ...parseRejectionSource(sourceReference),
          }
        : {}),
    };
    apiClient
      .request(
        'POST /v1/agents-v2/document-extraction-v2/resources/:resource_type/:resource_id/feedback',
        feedback,
      )
      .then(() => {
        setConfirmOpen(false);
        setDecided(decision);
        onReviewed?.(resource, decision);
        notify(
          formatMessage(
            decision === 'approved'
              ? messages.approveSuccess
              : messages.rejectSuccess,
          ),
          'success',
        );
      })
      .catch(() => {
        notify(formatMessage(messages.error), 'error');
      })
      .finally(() => {
        setDecisionInFlight(undefined);
      });
  };

  const confirm = (decision: MemberExtractionReviewDecision) => {
    setConfirmDecision(decision);
    setReasonId(undefined);
    setReasonDetail('');
    setConfirmOpen(true);
  };

  const dismiss = () => {
    if (!decisionInFlight) {
      setConfirmOpen(false);
    }
  };

  const detail = reason?.detail;
  const detailProps = detail
    ? {
        fullWidth: true,
        label: formatMessage(detail.label),
        showRequiredLabel: detail.required,
        value: reasonDetail,
        disabled: !!decisionInFlight,
      }
    : undefined;

  return (
    <div style={actionsStyle}>
      <Button variant="outlined" onClick={() => confirm('approved')}>
        {formatMessage(messages.approve)}
      </Button>
      <Button
        variant="outlined"
        color="negative"
        onClick={() => confirm('rejected')}
      >
        {formatMessage(messages.reject)}
      </Button>
      <Modal
        isOpen={confirmOpen}
        onDismiss={dismiss}
        size={1}
        title={formatMessage(
          isReject ? messages.rejectTitle : messages.approveTitle,
        )}
        actions={
          <>
            <Button
              variant="text"
              disabled={!!decisionInFlight}
              onClick={dismiss}
            >
              {formatMessage(messages.cancel)}
            </Button>
            <Button
              variant="contained"
              color={isReject ? 'negative' : 'default'}
              disabled={!canSubmit}
              onClick={() => submit(confirmDecision)}
              {...getTestProps('member-extraction-review-confirm')}
            >
              {formatMessage(isReject ? messages.reject : messages.approve)}
            </Button>
          </>
        }
      >
        <div style={confirmContentStyle}>
          <div style={resultSummaryStyle}>
            <Text size="subbody" weight="bold">
              {resource.title}
            </Text>
            {resource.value ? (
              <Text
                size="caption"
                style={{ color: theme.palette.primary.main }}
              >
                {resource.value}
              </Text>
            ) : null}
          </div>
          {isReject ? (
            <Select
              fullWidth
              showRequiredLabel
              label={formatMessage(messages.reasonLabel)}
              placeholder={formatMessage(messages.reasonPlaceholder)}
              value={reasonId ?? ''}
              disabled={!!decisionInFlight}
              onChange={value => {
                setReasonId(value as MemberExtractionRejectionReasonId);
                setReasonDetail('');
              }}
            >
              {reasons.map(item => (
                <SelectOption
                  key={item.id}
                  value={item.id}
                  title={formatMessage(item.label)}
                />
              ))}
            </Select>
          ) : null}
          {isReject && detail && detailProps ? (
            detail.kind === 'multiline' ? (
              <TextArea
                {...detailProps}
                rows={3}
                onChange={event => setReasonDetail(event.target.value)}
              />
            ) : (
              <TextField
                {...detailProps}
                type={detail.kind === 'date' ? 'date' : 'text'}
                onChange={event => setReasonDetail(event.target.value)}
              />
            )
          ) : null}
          <Text size="subbody">
            {formatMessage(
              isReject ? messages.rejectMessage : messages.approveMessage,
            )}
          </Text>
        </div>
      </Modal>
    </div>
  );
};
